import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type ChangePasswordFormData = {
  password: string;
  confirmPassword: string;
};

export const Route = createFileRoute("/change-password")({
  component: ChangePassword,
});

function ChangePassword() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    watch,
  } = useForm<ChangePasswordFormData>();

  const password = watch("password");

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if we have access token in hash (from password reset email)
        const hashParams = new URLSearchParams(
          window.location.hash.substring(1),
        );
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");
        const type = hashParams.get("type");

        console.log("=== CHANGE PASSWORD PAGE DEBUG ===");
        console.log("URL:", window.location.href);
        console.log("Type:", type);
        console.log("Access token exists:", !!accessToken);

        if (type === "recovery" && accessToken && refreshToken) {
          // Set the session from recovery link
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            console.error("Failed to set session:", error);
            toast.error("Link invalid", {
              description:
                "This link is invalid or expired. Please send reset email again.",
            });
            navigate({ to: "/login" });
            return;
          }

          // Clear the hash to clean up the URL
          window.history.replaceState(null, "", window.location.pathname);
        }

        // Get current session
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (!session) {
          toast.error("Please use the password reset link from your email");
          navigate({ to: "/login" });
          return;
        }

        setEmail(session.user.email || "");
      } catch (error) {
        console.error("Error checking auth:", error);
        toast.error("An error occurred");
        navigate({ to: "/login" });
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const onSubmit = async (data: ChangePasswordFormData) => {
    try {
      console.log("=== UPDATING PASSWORD ===");

      // Update the user's password
      const { error: updateError } = await supabase.auth.updateUser({
        password: data.password,
      });

      if (updateError) {
        setError("root", {
          type: "manual",
          message: updateError.message,
        });
        return;
      }

      // Sign out the user
      await supabase.auth.signOut();

      toast.success("Password updated", {
        description: "Please log in with new password.",
      });

      // Redirect to login page
      navigate({ to: "/login" });
    } catch (error) {
      console.error("Error changing password:", error);
      setError("root", {
        type: "manual",
        message: "An unexpected error occurred",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-svh w-full items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Change your password</CardTitle>
              <CardDescription>
                {email ? (
                  <>Enter a new password for {email}</>
                ) : (
                  <>Enter your new password below</>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-6">
                  <div className="grid gap-3">
                    <Label htmlFor="password">New Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your new password"
                      {...register("password", {
                        required: "Password is required",
                        minLength: {
                          value: 6,
                          message: "Password must be at least 6 characters",
                        },
                      })}
                    />
                    {errors.password && (
                      <p className="text-sm text-red-600">
                        {errors.password.message}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-3">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm your new password"
                      {...register("confirmPassword", {
                        required: "Please confirm your password",
                        validate: (value) =>
                          value === password || "Passwords do not match",
                      })}
                    />
                    {errors.confirmPassword && (
                      <p className="text-sm text-red-600">
                        {errors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                  {errors.root && (
                    <p className="text-sm text-red-600">
                      {errors.root.message}
                    </p>
                  )}
                  <div className="flex flex-col gap-3">
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? "Changing password..."
                        : "Change Password"}
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
